import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import type { Project } from "@/types/project";
import type { GearItem } from "@/types/gear";
import type { CrewMember } from "@/types/crew";
import type { ExportOptions } from "./ExportOptionsDialog";
import { useTranslation } from "react-i18next";

// Register fonts
Font.register({
  family: "Brakle",
  fonts: [
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-regular-webfont.woff",
      fontWeight: 400,
    },
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-medium-webfont.woff",
      fontWeight: 500,
    },
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-semibold-webfont.woff",
      fontWeight: 600,
    },
    {
      src: "https://hlahqfmphbtoobvmzaqg.supabase.co/storage/v1/object/public/fonts/brakle-bold-webfont.woff",
      fontWeight: 700,
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontFamily: "Helvetica",
    backgroundColor: "#FFFFFF",
    fontSize: 10,
  },

  // Top bar with date/time
  topBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  printedDate: {
    fontSize: 8,
    color: "#8E9196",
    textAlign: "right",
  },

  // Header section: project info left, crew right
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  leftColumn: {
    width: "60%",
    borderWidth: 1,
    borderColor: "#E5DEFF",
    borderRadius: 4,
    padding: 6,
    marginRight: 4,
  },

  // Project title and description
  projectTitle: {
    fontFamily: "Brakle",
    fontWeight: 700,
    fontSize: 12,
    color: "#1A1F2C",
    marginBottom: 4,
  },
  descriptionText: {
    fontSize: 9,
    color: "#221F26",
    lineHeight: 1.3,
    marginBottom: 4,
    flexWrap: "wrap",
  },

  // A compact "label + text" approach
  infoLine: {
    marginBottom: 2,
    flexDirection: "row",
  },
  label: {
    fontWeight: "bold",
    marginRight: 4,
  },
  text: {
    color: "#221F26",
    fontSize: 9,
    lineHeight: 1.3,
  },

  // Crew
  crewHeader: {
    fontFamily: "Brakle",
    fontWeight: 500,
    fontSize: 10,
    color: "#1A1F2C",
    marginBottom: 4,
  },
  crewMember: {
    marginBottom: 3,
    borderWidth: 1,
    borderColor: "#E5DEFF",
    borderRadius: 3,
    padding: 4,
  },
  crewName: {
    fontFamily: "Brakle",
    fontWeight: 500,
    fontSize: 9,
    color: "#1A1F2C",
  },
  crewRole: {
    fontSize: 8,
    color: "#7E69AB",
  },
  crewContact: {
    fontSize: 8,
    color: "#221F26",
  },

  // Notes sections
  notesSection: {
    marginTop: 4,
  },
  notesLabel: {
    fontFamily: "Brakle",
    fontWeight: 500,
    color: "#8B5CF6",
    fontSize: 9,
    marginBottom: 2,
  },
  notesText: {
    fontSize: 9,
    color: "#221F26",
    lineHeight: 1.3,
  },

  // Table
  table: {
    display: "flex",
    width: "100%",
    borderWidth: 1,
    borderColor: "#E5DEFF",
    borderRadius: 4,
    overflow: "hidden",
  },
  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5DEFF",
    minHeight: 20,
    paddingHorizontal: 3,
  },
  tableHeader: {
    backgroundColor: "#F1F0FB",
  },
  tableCell: {
    flex: 1,
    paddingVertical: 4,
    color: "#221F26",
    fontSize: 9,
  },
  tableCellNarrow: {
    width: 36,
    textAlign: "center",
  },
  tableCellWide: {
    flex: 3,
  },
  itemNotes: {
    color: "#6B7280",
    fontSize: 8,
    fontStyle: "italic",
  },

  // Totals
  totalsRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 4,
    marginBottom: 6,
  },
  totalsLabel: {
    color: "#7E69AB",
    fontFamily: "Brakle",
    fontWeight: 500,
    marginRight: 4,
    fontSize: 9,
  },
  totalsValue: {
    color: "#221F26",
    fontFamily: "Brakle",
    fontWeight: 600,
    fontSize: 9,
  },

  // Photos
  photosHeader: {
    fontFamily: "Brakle",
    fontWeight: 600,
    fontSize: 11,
    color: "#1A1F2C",
    marginTop: 10,
    marginBottom: 4,
  },
  photosGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 8,
  },
  photoWrapper: {
    width: "31%",
    aspectRatio: "1",
  },
  photo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: 4,
  },

  // Footer
  footerContainer: {
    position: "absolute",
    bottom: 20,
    left: 0,
    right: 0,
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: 4,
  },
  footerNote: {
    fontSize: 8,
    color: "#8E9196",
    fontStyle: "italic",
  },
  footerLogo: {
    width: 12,
    height: 12,
  },
  departmentHeader: {
    fontFamily: "Brakle",
    fontWeight: 600,
    fontSize: 11,
    color: "#1A1F2C",
    marginTop: 10,
    marginBottom: 4,
  },
  categoryBlock: {
    marginBottom: 8,
  },
  categoryHeader: {
    fontFamily: "Brakle",
    fontWeight: 500,
    fontSize: 10,
    color: "#1A1F2C",
    marginBottom: 4,
  },
  rightColumn: {
    width: "38%",
    padding: 6,
  },
  departmentSection: {
    // Note: @react-pdf/renderer doesn't support pageBreakInside
    // We'll rely on the library's default page breaking behavior
  },
});

// Reorder categories if needed
const cameraDepartmentCategories = [
  "Cameras",
  "Lenses",
  "Filters",
  "Camera AKS",
  "Monitoring",
  "Power",
  "Camera Support",
  "Media",
  "Other",
] as const;

const geCategories = [
  "Lighting Fixtures",
  "Grip",
  "Electric",
  "Grip Package",
  "Specialty Items",
] as const;

function renderDepartmentTable(
  title: string,
  categories: readonly string[],
  items: GearItem[],
  total: number,
  showBudget: boolean,
  currency: string
) {
  const itemsByCategory = categories.reduce((acc, category) => {
    const categoryItems = items.filter((item) => item.category === category);
    if (categoryItems.length > 0) acc[category] = categoryItems;
    return acc;
  }, {} as Record<string, GearItem[]>);

  if (Object.keys(itemsByCategory).length === 0) return null;

  return (
    <View style={styles.departmentSection}>
      <Text style={styles.departmentHeader}>{title}</Text>
      {Object.entries(itemsByCategory).map(([category, categoryItems]) => (
        <View style={styles.categoryBlock} key={category}>
          <Text style={styles.categoryHeader}>{category}</Text>
          <View style={styles.table}>
            <View style={[styles.tableRow, styles.tableHeader]}>
              <Text style={[styles.tableCell, styles.tableCellNarrow]}>
                Qty
              </Text>
              <Text style={[styles.tableCell, styles.tableCellWide]}>Item</Text>
              {showBudget && (
                <>
                  <Text style={styles.tableCell}>Rate</Text>
                  <Text style={styles.tableCell}>Days</Text>
                  <Text style={styles.tableCell}>Total</Text>
                </>
              )}
              <Text style={styles.tableCell}>Vendor</Text>
            </View>
            {categoryItems.map((item) => {
              const lineTotal =
                showBudget && item.cost_per_unit
                  ? (item.quantity || 1) *
                    item.cost_per_unit *
                    (item.days_needed || 1)
                  : null;
              return (
                <>
                  <View key={item.id} style={styles.tableRow}>
                    <Text style={[styles.tableCell, styles.tableCellNarrow]}>
                      {item.quantity || 1}
                    </Text>
                    <Text style={[styles.tableCell, styles.tableCellWide]}>
                      {item.name}
                      {item.notes && (
                        <>
                          {"\n"}
                          <Text style={styles.itemNotes}>
                            Note: {item.notes}
                          </Text>
                        </>
                      )}
                    </Text>
                    {showBudget && (
                      <>
                        <Text style={styles.tableCell}>
                          {item.cost_per_unit
                            ? formatCurrency(item.cost_per_unit, currency)
                            : "N/A"}
                        </Text>
                        <Text style={styles.tableCell}>
                          {item.days_needed || 1}
                        </Text>
                        <Text style={styles.tableCell}>
                          {lineTotal
                            ? formatCurrency(lineTotal, currency)
                            : "N/A"}
                        </Text>
                      </>
                    )}
                    <Text style={styles.tableCell}>
                      {item.vendors?.name || "-"}
                    </Text>
                  </View>
                  <Text style={[styles.tableCell, styles.tableCellNarrow]}>
                    {item.quantity || 1}
                  </Text>
                  {showBudget && (
                    <>
                      <Text style={[styles.tableCell, styles.tableCellNarrow]}>
                        {item.cost_per_unit
                          ? formatCurrency(item.cost_per_unit)
                          : "N/A"}
                      </Text>
                      <Text style={[styles.tableCell, styles.tableCellNarrow]}>
                        {item.days_needed || 1}
                      </Text>
                      <Text style={[styles.tableCell, styles.tableCellNarrow]}>
                        {item.cost_per_unit && item.days_needed
                          ? formatCurrency(
                              item.cost_per_unit * item.days_needed
                            )
                          : "N/A"}
                      </Text>
                    </>
                  )}
                  <Text style={[styles.tableCell]}>
                    {item.vendors?.name || "-"}
                  </Text>
                </>
              );
            })}
          </View>
        </View>
      ))}
      {showBudget && total > 0 && (
        <View style={styles.totalsRow}>
          <Text style={styles.totalsLabel}>Dept Total:</Text>
          <Text style={styles.totalsValue}>
            {formatCurrency(total, currency)}
          </Text>
        </View>
      )}
    </View>
  );
}

interface ProjectPDFProps {
  project: Project;
  exportOptions: ExportOptions;
  currency: string;
}

export function ProjectPDF({
  project,
  exportOptions,
  currency,
}: ProjectPDFProps) {
  const [gearItems, setGearItems] = useState<GearItem[]>([]);
  const [crewMembers, setCrewMembers] = useState<CrewMember[]>([]);
  const [vendorName, setVendorName] = useState<string>("");
  const [totals, setTotals] = useState({
    cameraTotal: 0,
    geTotal: 0,
    overallTotal: 0,
  });
  const { t } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      // Construct the query based on export options
      let query = supabase
        .from("gear_items")
        .select("*, vendors(name)")
        .eq("project_id", project.id);

      // Filter by vendor if selected
      if (exportOptions.selectedVendor) {
        query = query.eq("vendor_id", exportOptions.selectedVendor);

        // Fetch vendor name
        const { data: vendorData } = await supabase
          .from("vendors")
          .select("name")
          .eq("id", exportOptions.selectedVendor)
          .single();

        if (vendorData) {
          setVendorName(vendorData.name);
        }
      }

      // Filter by department if specified
      const categories = [];
      if (exportOptions.includeCamera) {
        categories.push(...cameraDepartmentCategories);
      }
      if (exportOptions.includeGE) {
        categories.push(...geCategories);
      }

      if (categories.length > 0) {
        query = query.in("category", categories);
      }

      const { data: gearData } = await query.order("category");

      if (gearData) {
        setGearItems(gearData);

        // Calculate totals after setting gear items
        const cameraItems = gearData.filter((item) =>
          cameraDepartmentCategories.includes(item.category as any)
        );

        const geItems = gearData.filter((item) =>
          geCategories.includes(item.category as any)
        );

        const cameraTotal = cameraItems.reduce(
          (sum, item) =>
            sum +
            (item.cost_per_unit || 0) *
              (item.quantity || 1) *
              (item.days_needed || 1),
          0
        );

        const geTotal = geItems.reduce(
          (sum, item) =>
            sum +
            (item.cost_per_unit || 0) *
              (item.quantity || 1) *
              (item.days_needed || 1),
          0
        );

        setTotals({
          cameraTotal,
          geTotal,
          overallTotal: cameraTotal + geTotal,
        });
      }

      // Fetch crew if needed
      const { data: crewData } = await supabase
        .from("crew_members")
        .select("*")
        .eq("project_id", project.id)
        .order("role");
      if (crewData) setCrewMembers(crewData);
    };
    fetchData();
  }, [project.id, exportOptions]);

  // Format dates helper function with timezone handling
  const formatDate = (date: string | null) => {
    if (!date) return null;
    try {
      // Add one day to compensate for timezone offset
      const dateObj = new Date(date);
      dateObj.setDate(dateObj.getDate() + 1);
      return format(dateObj, "MMMM d, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return null;
    }
  };

  // Split crew members into two columns
  const midPoint = Math.ceil(crewMembers.length / 2);
  const leftColumnCrew = crewMembers.slice(0, midPoint);
  const rightColumnCrew = crewMembers.slice(midPoint);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Top bar */}
        <View style={styles.topBar}>
          <View />
          <Text style={styles.printedDate}>
            {t("project.pdf.printed_on")}:{" "}
            {format(new Date(), "MMMM d, yyyy h:mm a")}
          </Text>
        </View>

        {/* HEADER: Project Info + Crew */}
        <View style={styles.headerSection}>
          {/* Left column: Project info */}
          <View style={styles.leftColumn}>
            <Text style={styles.projectTitle}>
              {project.name} - {t("project.pdf.gear_list")}
              {vendorName && (
                <>
                  {"\n"}
                  <Text
                    style={{ fontSize: 10, color: "#7E69AB", marginTop: 4 }}
                  >
                    {vendorName}
                  </Text>
                </>
              )}
            </Text>

            {/* Only show project details if not a vendor export */}
            {!exportOptions.selectedVendor && (
              <>
                {project.description && (
                  <Text style={styles.descriptionText}>
                    {project.description}
                  </Text>
                )}

                {/* Budgets */}
                {exportOptions.includeBudget && project.budget_enabled && (
                  <>
                    {project.camera_budget > 0 && (
                      <View style={styles.infoLine}>
                        <Text style={[styles.label, styles.text]}>
                          {t("project.pdf.camera_budget")}:
                        </Text>
                        <Text style={styles.text}>
                          {formatCurrency(project.camera_budget, currency)}
                        </Text>
                      </View>
                    )}
                    {project.ge_budget > 0 && (
                      <View style={styles.infoLine}>
                        <Text style={[styles.label, styles.text]}>
                          {t("project.pdf.ge_budget")}:
                        </Text>
                        <Text style={styles.text}>
                          {formatCurrency(project.ge_budget, currency)}
                        </Text>
                      </View>
                    )}
                    {(project.camera_budget > 0 || project.ge_budget > 0) && (
                      <View style={styles.infoLine}>
                        <Text style={[styles.label, styles.text]}>
                          {t("project.pdf.total_budget")}:
                        </Text>
                        <Text style={styles.text}>
                          {formatCurrency(
                            (project.camera_budget || 0) +
                              (project.ge_budget || 0),
                            currency
                          )}
                        </Text>
                      </View>
                    )}
                  </>
                )}

                {/* Dates */}
                {project.prep_date && (
                  <View style={styles.infoLine}>
                    <Text style={[styles.label, styles.text]}>
                      {t("project.header.prep")}:
                    </Text>
                    <Text style={styles.text}>
                      {formatDate(project.prep_date)}
                    </Text>
                  </View>
                )}
                {project.pre_light_date && (
                  <View style={styles.infoLine}>
                    <Text style={[styles.label, styles.text]}>
                      {t("project.header.pre_light")}:
                    </Text>
                    <Text style={styles.text}>
                      {formatDate(project.pre_light_date)}
                    </Text>
                  </View>
                )}
                {project.shoot_dates && project.shoot_dates.length > 0 && (
                  <View style={styles.infoLine}>
                    <Text style={[styles.label, styles.text]}>
                      {t("project.header.shoot")}:
                    </Text>
                    <Text style={styles.text}>
                      {project.shoot_dates
                        .map((date) => formatDate(date))
                        .filter(Boolean)
                        .join(", ")}
                    </Text>
                  </View>
                )}

                {/* Notes sections */}
                {project.camera_notes && (
                  <View style={styles.notesSection}>
                    <Text style={styles.notesLabel}>
                      {t("project.pdf.camera_notes")}
                    </Text>
                    <Text style={styles.notesText}>{project.camera_notes}</Text>
                  </View>
                )}
                {project.ge_notes && (
                  <View style={styles.notesSection}>
                    <Text style={styles.notesLabel}>
                      {t("project.pdf.ge_notes")}
                    </Text>
                    <Text style={styles.notesText}>{project.ge_notes}</Text>
                  </View>
                )}
                {project.general_notes && (
                  <View style={styles.notesSection}>
                    <Text style={styles.notesLabel}>
                      {t("project.details.general_notes")}
                    </Text>
                    <Text style={styles.notesText}>
                      {project.general_notes}
                    </Text>
                  </View>
                )}
              </>
            )}
          </View>

          {/* Right column: Crew list - Only show if not a vendor export */}
          {!exportOptions.selectedVendor && (
            <View style={styles.rightColumn}>
              {crewMembers.length > 0 ? (
                <>
                  <Text style={styles.crewHeader}>{t("project.pdf.crew")}</Text>
                  <View style={{ flexDirection: "row", gap: 4 }}>
                    {/* Left column of crew */}
                    <View style={{ flex: 1 }}>
                      {leftColumnCrew.map((member) => (
                        <View key={member.id} style={styles.crewMember}>
                          <Text style={styles.crewName}>{member.name}</Text>
                          <Text style={styles.crewRole}>{member.role}</Text>
                          {member.phone && (
                            <Text style={styles.crewContact}>
                              {t("crew.member.phone")}: {member.phone}
                            </Text>
                          )}
                          {member.email && (
                            <Text style={styles.crewContact}>
                              {t("crew.member.email")}: {member.email}
                            </Text>
                          )}
                        </View>
                      ))}
                    </View>
                    {/* Right column of crew */}
                    <View style={{ flex: 1 }}>
                      {rightColumnCrew.map((member) => (
                        <View key={member.id} style={styles.crewMember}>
                          <Text style={styles.crewName}>{member.name}</Text>
                          <Text style={styles.crewRole}>{member.role}</Text>
                          {member.phone && (
                            <Text style={styles.crewContact}>
                              {t("crew.member.phone")}: {member.phone}
                            </Text>
                          )}
                          {member.email && (
                            <Text style={styles.crewContact}>
                              {t("crew.member.email")}: {member.email}
                            </Text>
                          )}
                        </View>
                      ))}
                    </View>
                  </View>
                </>
              ) : (
                <Text style={styles.crewHeader}>
                  {t("project.pdf.no_crew_assigned")}
                </Text>
              )}
            </View>
          )}
        </View>

        {/* PHOTOS SECTION - Only show if not a vendor export */}
        {!exportOptions.selectedVendor &&
          project.scout_photos &&
          project.scout_photos.length > 0 && (
            <>
              <Text style={styles.photosHeader}>
                {t("project.pdf.location_photos")}
              </Text>
              <View style={styles.photosGrid}>
                {project.scout_photos.slice(0, 6).map((photoUrl, i) => (
                  <View key={i} style={styles.photoWrapper}>
                    <Image src={photoUrl} style={styles.photo} />
                  </View>
                ))}
              </View>
            </>
          )}

        {/* Filter departments based on options */}
        {exportOptions.includeCamera &&
          renderDepartmentTable(
            "Camera Equipment",
            cameraDepartmentCategories,
            gearItems.filter((item) =>
              cameraDepartmentCategories.includes(item.category as any)
            ),
            totals.cameraTotal,
            exportOptions.includeBudget,
            currency
          )}

        {exportOptions.includeGE &&
          renderDepartmentTable(
            "G&E Equipment",
            geCategories,
            gearItems.filter((item) =>
              geCategories.includes(item.category as any)
            ),
            totals.geTotal,
            exportOptions.includeBudget,
            currency
          )}

        {/* Only show overall total if both departments are included and budget is enabled */}
        {exportOptions.includeBudget &&
          project.budget_enabled &&
          exportOptions.includeCamera &&
          exportOptions.includeGE &&
          totals.overallTotal > 0 && (
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>
                {t("project.pdf.overall_total")}:
              </Text>
              <Text style={styles.totalsValue}>
                {formatCurrency(totals.overallTotal, currency)}
              </Text>
            </View>
          )}

        {/* Footer with logo and text */}
        <View style={styles.footerContainer}>
          <Image
            src="/lovable-uploads/f372cafa-8375-4a25-a4cf-39023bb641a8.png"
            style={styles.footerLogo}
          />
          <Text style={styles.footerNote}>{t("project.pdf.footer_note")}</Text>
        </View>
      </Page>
    </Document>
  );
}
